import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getProjects, createProject, updateProject, deleteProject, uploadProjectMapPdf, deleteProjectMapPdf, getProjectMapPdfUrl } from '../utils/api';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Trash2, LayoutList, Building2, FileText, Upload, Eye } from 'lucide-react';
import toast from 'react-hot-toast';

const EMPTY = { name: '', type: '', contact: '', map_query: '', map_lat: '', map_lng: '', address: '' };

function MapPdfUploader({ project }) {
  const qc = useQueryClient();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const uploadMutation = useMutation({
    mutationFn: (file) => uploadProjectMapPdf(project.id, file),
    onMutate: () => setUploading(true),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Map PDF uploaded');
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Upload failed'),
    onSettled: () => setUploading(false),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteProjectMapPdf(project.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Map PDF removed');
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Error'),
  });

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error('Only PDF files are allowed');
      e.target.value = '';
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error('File is too large (max 15MB)');
      e.target.value = '';
      return;
    }
    uploadMutation.mutate(file);
    e.target.value = ''; // allow re-selecting the same file later
  };

  return (
    <div className="form-group">
      <label className="form-label">Map / Layout PDF</label>
      <div style={{
        border: '1.5px dashed var(--border)', borderRadius: 8, padding: 14,
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: 8, background: project.has_map_pdf ? 'var(--green-light)' : 'var(--gray-light)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <FileText size={17} color={project.has_map_pdf ? 'var(--green-dark)' : 'var(--text3)'} />
        </div>

        <div style={{ flex: 1, minWidth: 160 }}>
          {project.has_map_pdf ? (
            <>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{project.map_pdf_filename || 'project-map.pdf'}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                Uploaded {project.map_pdf_uploaded_at ? new Date(project.map_pdf_uploaded_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
              </div>
            </>
          ) : (
            <div style={{ fontSize: 12.5, color: 'var(--text3)' }}>No map PDF uploaded yet</div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {project.has_map_pdf && (
            <>
              <a href={getProjectMapPdfUrl(project.id)} target="_blank" rel="noreferrer" className="btn btn-outline btn-sm btn-icon" title="View">
                <Eye size={13} />
              </a>
              <button type="button" className="btn btn-sm btn-icon" onClick={() => deleteMutation.mutate()} title="Remove"
                style={{ background: 'var(--red-light)', color: 'var(--red)', border: 'none' }}>
                <Trash2 size={13} />
              </button>
            </>
          )}
          <button type="button" className="btn btn-outline btn-sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
            <Upload size={13} /> {uploading ? 'Uploading...' : project.has_map_pdf ? 'Replace' : 'Upload PDF'}
          </button>
          <input ref={fileInputRef} type="file" accept="application/pdf" onChange={handleFileChange} style={{ display: 'none' }} />
        </div>
      </div>
      <p style={{ fontSize: 11.5, color: 'var(--text3)', marginTop: 4 }}>
        Visible to everyone (public dashboard + sales team) via the Project Map button. PDF only, max 15MB.
      </p>
    </div>
  );
}

function ProjectModal({ project, onClose }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(project ? {
    ...project,
    _mapsLink: project.map_query || '',  // show whatever was saved as the maps link
  } : EMPTY);
  const isEdit = !!project?.id;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const mutation = useMutation({
    mutationFn: isEdit ? (data) => updateProject(project.id, data) : createProject,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      toast.success(isEdit ? 'Project updated' : 'Project created');
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Error'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.type) return toast.error('Name and type required');
    mutation.mutate({
      ...form,
      map_lat: form.map_lat ? parseFloat(form.map_lat) : null,
      map_lng: form.map_lng ? parseFloat(form.map_lng) : null,
    });
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-lg">
        <div className="modal-head">
          <h3>{isEdit ? 'Edit Project' : 'Add New Project'}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="form-group">
                <label className="form-label">Project Name *</label>
                <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Shiv Residency" required />
              </div>
              <div className="form-group">
                <label className="form-label">Type *</label>
                <select className="form-select" value={form.type} onChange={e => set('type', e.target.value)} required>
                  <option value="">Select type</option>
                  <option>Residential Plots</option>
                  <option>Residential & Commercial</option>
                  <option>Commercial</option>
                  <option>Agricultural</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Contact Number</label>
                <input className="form-input" value={form.contact} onChange={e => set('contact', e.target.value)} placeholder="9166523869" />
              </div>
              <div className="form-group">
                <label className="form-label">Google Maps Link</label>
                <input
                  className="form-input"
                  value={form._mapsLink || ''}
                  onChange={e => {
                    const val = e.target.value;
                    set('_mapsLink', val);
                    // Auto-extract lat/lng from a Google Maps share URL
                    // Handles formats: @lat,lng  and  ll=lat,lng  and  query=lat,lng
                    const match = val.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/) ||
                                  val.match(/[?&]ll=(-?\d+\.?\d*),(-?\d+\.?\d*)/) ||
                                  val.match(/[?&]query=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
                    if (match) {
                      set('map_lat', match[1]);
                      set('map_lng', match[2]);
                    }
                    // Store the full URL as map_query too for the maps popup
                    set('map_query', val);
                  }}
                  placeholder="Paste Google Maps link here"
                />
                <p style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>
                  <strong>How to get the link:</strong> Open Google Maps on your phone → search the project location → tap the location pin → tap <em>Share</em> → tap <em>Copy Link</em> → paste here.<br />
                  Short links (maps.app.goo.gl/...) and full links both work correctly.{' '}
                  <a href="https://maps.google.com" target="_blank" rel="noreferrer"
                    style={{ color: 'var(--blue)' }}>Open Google Maps →</a>
                </p>
              </div>
            </div>

            {/* Show extracted coordinates (read-only confirmation) */}
            {(form.map_lat || form.map_lng) && (
              <div style={{
                background: 'var(--green-light)', borderRadius: 8, padding: '10px 14px',
                fontSize: 12, color: 'var(--green-dark)', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4
              }}>
                ✅ Location extracted — Lat: <strong>{form.map_lat}</strong> &nbsp; Lng: <strong>{form.map_lng}</strong>
                &nbsp;
                <a href={`https://www.google.com/maps?q=${form.map_lat},${form.map_lng}`}
                  target="_blank" rel="noreferrer" style={{ color: 'var(--green-dark)', fontWeight: 600 }}>
                  Verify on Map →
                </a>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              {/* Hidden lat/lng inputs — values managed by the Google Maps link extractor above */}
              <input type="hidden" value={form.map_lat || ''} />
              <input type="hidden" value={form.map_lng || ''} />
            </div>
            <div className="form-group">
              <label className="form-label">Address</label>
              <textarea className="form-textarea" value={form.address || ''} onChange={e => set('address', e.target.value)} placeholder="Full project address..." />
            </div>

            {isEdit && <MapPdfUploader project={project} />}
            {!isEdit && (
              <p style={{ fontSize: 11.5, color: 'var(--text3)' }}>
                You can upload a map/layout PDF after creating the project, from its Edit screen.
              </p>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-green btn-sm" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminProjects() {
  const qc = useQueryClient();
  const [modal, setModal] = useState(null); // null | 'new' | project object
  const { data: projects = [], isLoading } = useQuery({ queryKey: ['projects'], queryFn: getProjects });

  const delMutation = useMutation({
    mutationFn: deleteProject,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['projects'] }); toast.success('Project deleted'); },
    onError: (err) => toast.error(err.response?.data?.error || 'Error'),
  });

  const handleDelete = (proj) => {
    if (!window.confirm(`Delete "${proj.name}"? This cannot be undone.`)) return;
    delMutation.mutate(proj.id);
  };

  return (
    <div style={{ padding: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700 }}>Projects</h1>
          <p style={{ color: 'var(--text3)', fontSize: 13, marginTop: 2 }}>Manage all real estate projects</p>
        </div>
        <button className="btn btn-green" onClick={() => setModal('new')}>
          <Plus size={16} /> Add Project
        </button>
      </div>

      <div className="card">
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div className="spinner" /></div>
        ) : projects.length === 0 ? (
          <div className="empty-state"><Building2 size={40} /><p>No projects yet. Add one to get started.</p></div>
        ) : (
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Total</th>
                  <th>Available</th>
                  <th>Booked</th>
                  <th>Contact</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {projects.map(p => (
                  <tr key={p.id}>
                    <td style={{ fontWeight: 600 }}>{p.name}</td>
                    <td style={{ fontSize: 12, color: 'var(--text2)' }}>{p.type}</td>
                    <td className="mono">{p.total}</td>
                    <td><span className="badge badge-available">{p.available}</span></td>
                    <td><span className="badge badge-booked">{p.booked}</span></td>
                    <td style={{ fontSize: 12 }}>📞 {p.contact}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <Link to={`/admin/projects/${p.id}/plots`} className="btn btn-outline btn-sm btn-icon" title="Manage Plots">
                          <LayoutList size={14} />
                        </Link>
                        <button className="btn btn-outline btn-sm btn-icon" onClick={() => setModal(p)} title="Edit">
                          <Pencil size={14} />
                        </button>
                        <button className="btn btn-sm btn-icon" onClick={() => handleDelete(p)} title="Delete"
                          style={{ background: 'var(--red-light)', color: 'var(--red)', border: 'none' }}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <ProjectModal
          project={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
